/**
 * Persistance du stockage — EF-PLT-005, ENF-020.
 *
 * La demande est faite lors de la **première modification métier** enregistrée
 * localement, et non au premier lancement. L'application ne doit jamais
 * affirmer que les données sont conservées de manière définitive : la décision
 * appartient au navigateur.
 */

export type PersistenceStatus = 'accordée' | 'refusée' | 'indéterminée';

const STATUS_MESSAGES: Readonly<Record<PersistenceStatus, string>> = {
  accordée: "L'origine dispose d'un stockage persistant.",
  refusée:
    'Le navigateur peut effacer les données locales. Exportez régulièrement votre roadbook.',
  indéterminée:
    "L'état de persistance n'a pu être déterminé. Exportez régulièrement votre roadbook.",
};

export function persistenceMessage(status: PersistenceStatus): string {
  return STATUS_MESSAGES[status];
}

function storageManager(): StorageManager | undefined {
  return globalThis.navigator?.storage;
}

/** Lit l'état courant sans rien demander. */
export async function readPersistenceStatus(): Promise<PersistenceStatus> {
  const storage = storageManager();
  if (storage === undefined || typeof storage.persisted !== 'function') return 'indéterminée';
  try {
    return (await storage.persisted()) ? 'accordée' : 'refusée';
  } catch {
    return 'indéterminée';
  }
}

/** Demande le stockage persistant. Un refus ne bloque jamais l'utilisation. */
export async function requestPersistence(): Promise<PersistenceStatus> {
  const storage = storageManager();
  if (storage === undefined || typeof storage.persist !== 'function') return 'indéterminée';
  try {
    if (typeof storage.persisted === 'function' && (await storage.persisted())) return 'accordée';
    return (await storage.persist()) ? 'accordée' : 'refusée';
  } catch {
    return 'indéterminée';
  }
}

/** Formate un volume d'octets pour l'affichage des réglages. */
export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace('.', ',')} ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2).replace('.', ',')} Go`;
}
